/**
 * GeneralTabSummaryCard Component
 *
 * Renders the Summary card for the General tab, displaying:
 * - Company description, founding year, WZ 2008 code, value chain, ownership
 * - HQ location with optional Google Maps link
 * - Sales row (when user can't view extended data)
 * - Number of employees (single value or array table)
 * - Top management table
 * - Loading and error states
 *
 * CALCULATION OWNERSHIP: This component receives all data and formatting from parent.
 * Number formatting is done via formatNumber util imported directly.
 */

import PropTypes from 'prop-types';
import GeneralLoader from '../../../loaders/GeneralLoader';
import ChipsList from '../../../common/ChipsList';
// Parked with the VAT ID copy button below — see the comment on that row.
// import CopyButton from '../../../common/CopyButton';
import { PUNCTUATION } from '../../../../utils/constants/notation';
import { getListingStatusName } from '../../../../utils/ownershipCriteria';
import { getRegisterStatusText } from '../../../../utils/companyStatus';
import { FIELD_SOURCE, dataSourceClass } from '../../../../utils/constants/dataSources';
import SummarySalesRow from './SummarySalesRow';
import SummaryEmployeesRow from './SummaryEmployeesRow';
import SummaryIndustryCodeRow from './SummaryIndustryCodeRow';
import SummaryTopManagementRow from './SummaryTopManagementRow';

/**
 * TD-2510: the commercial-register entry reads as one fact, so the number and the
 * court render as a single value ("HRB 12300 B, Amtsgericht Charlottenburg" /
 * "FN 60562 m, Handelsgericht Wien") rather than two rows. Either half may be
 * missing — filtering before the join keeps a lone number from trailing a comma —
 * and both missing yields '' so the caller can drop the row entirely.
 *
 * `tradeRegisterNumber` already arrives with its register type prefixed (BE builds
 * type + number in `tradeRegisterDisplaySql`), so nothing is prefixed again here.
 *
 * The court arrives from a different payload than the number. `tradeRegisterNumber`
 * and `vatId` come camelCased off the basic-info read (`getRawColumnsForSelect`),
 * which has no court column at all; the court exists only on the company-detail
 * query, as snake_case `trade_register_court`, and that payload stays a separate
 * object the card never receives (see the `registerCourt` prop). Hence two
 * sources, camel first: if the basic-info read ever grows a `tradeRegisterCourt`
 * column, it wins and the prop becomes dead weight rather than needing a revert.
 */
const formatCommercialRegister = (company, registerCourt) =>
  [company.tradeRegisterNumber, company.tradeRegisterCourt || registerCourt]
    .filter(Boolean)
    .join(', ');

/**
 * @param {Object} props
 * @param {Object} props.company - Company data object
 * @param {boolean} props.isLoadingSupplementaryData - Whether supplementary data is loading
 * @param {Error|null} props.loadSupplementaryDataError - Error from loading supplementary data
 * @param {Object|null} props.industryCode - Resolved WZ code `{ code, name, divisionName }`
 * @param {boolean} props.isLoadingDetails - Whether details are loading
 * @param {Array} props.topManagement - Array of top management persons
 * @param {boolean} props.canViewExtendedData - Whether user can view extended profiler data
 * @param {string} props.companyGoogleMapsLink - Google Maps URL for HQ location
 */
const GeneralTabSummaryCard = ({
  company = {},
  registerCourt = '',
  isLoadingSupplementaryData = false,
  loadSupplementaryDataError = null,
  industryCode = null,
  isLoadingDetails = false,
  topManagement = [],
  canViewExtendedData = true,
  companyGoogleMapsLink = '',
  ownershipSummary = null
}) => {
  const isLoading = isLoadingSupplementaryData || loadSupplementaryDataError;
  // Parsed listing status wins over legacy `company.ownership`; unknown values fall back to it.
  const ownershipText =
    getListingStatusName((ownershipSummary || company.ownershipSummary)?.listingStatus) ||
    company.ownership;

  // Show sales row when user can't view extended data (financials card shows sales)
  const showSalesRow = !canViewExtendedData;

  const foundingYearDisplay = company.foundingYear || PUNCTUATION.EMPTY_VALUE;

  // TD-2510: identifier rows are hidden outright when the company has no such
  // value, rather than rendering an "n/a" placeholder — an absent register entry
  // is unremarkable, and a card of empty identifier rows reads as broken data.
  // The register status is a register fact too, so it joins the same row.
  const registerRowValue = [
    formatCommercialRegister(company, registerCourt),
    getRegisterStatusText(company)
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="card">
      <div className="title">Summary</div>

      {/* Description Row */}
      <div className="card-row">
        <div className={`property ${dataSourceClass(FIELD_SOURCE.description)}`}>Description</div>
        <div className="value description">
          {isLoading ? <GeneralLoader /> : company.description || PUNCTUATION.EMPTY_VALUE}
        </div>
      </div>

      {/* Entity Founding Year Row */}
      <div className="card-row">
        <div className={`property ${dataSourceClass(FIELD_SOURCE.foundingYear)}`}>
          Entity founding year
        </div>
        <div className="value">{isLoading ? <GeneralLoader /> : foundingYearDisplay}</div>
      </div>

      <SummaryIndustryCodeRow industryCode={industryCode} isLoading={!!isLoading} />

      {/* Value Chain Row */}
      <div className="card-row">
        <div className={`property ${dataSourceClass(FIELD_SOURCE.valueChain)}`}>Value chain</div>
        <div className="value value-chain">
          {isLoading ? (
            <GeneralLoader />
          ) : (
            <>
              {company.valueChain?.length ? (
                <ChipsList list={company.valueChain} visibleCount={88888} />
              ) : (
                PUNCTUATION.EMPTY_VALUE
              )}
            </>
          )}
        </div>
      </div>

      {/* Ownership Row (conditional) */}
      {!!ownershipText && (
        <div className="card-row">
          <div className={`property ${dataSourceClass(FIELD_SOURCE.ownership)}`}>Ownership</div>
          <div className="value">{ownershipText}</div>
        </div>
      )}

      {/* HQ Location Row */}
      <div className="card-row">
        <div className={`property ${dataSourceClass(FIELD_SOURCE.hqLocation)}`}>HQ location</div>
        <div className="value">
          {!!companyGoogleMapsLink && (
            <a
              className="company-google-maps-link"
              href={companyGoogleMapsLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                window.open(companyGoogleMapsLink, '_blank');
              }}
            >
              {company.headquarters}
            </a>
          )}
          {!companyGoogleMapsLink && <>{company.headquarters || PUNCTUATION.EMPTY_VALUE}</>}
        </div>
      </div>

      {/* Commercial Register Row (TD-2510, conditional) — number + court as one value */}
      {!isLoading && !!registerRowValue && (
        <div className="card-row">
          <div className={`property ${dataSourceClass(FIELD_SOURCE.identifiers)}`}>Register</div>
          <div className="value">{registerRowValue}</div>
        </div>
      )}

      {/* VAT ID Row (TD-2510, conditional).
          The copy-to-clipboard affordance is parked, not deleted (PO call, TD-2510
          review): the VAT ID is the one identifier here that gets pasted into a CRM,
          so the button may come back. Re-enable by restoring the import and the
          <CopyButton /> line below — components/common/CopyButton.jsx and its tests
          are still in the tree, and `.value-with-copy` still carries the flex layout
          the button needs. */}
      {!isLoading && !!company.vatId && (
        <div className="card-row">
          <div className={`property ${dataSourceClass(FIELD_SOURCE.identifiers)}`}>VAT ID</div>
          <div className="value value-with-copy">
            {company.vatId}
            {/* <CopyButton value={company.vatId} label="VAT ID" /> */}
          </div>
        </div>
      )}

      {/* Sales Row (when user can't view extended data) */}
      {showSalesRow && <SummarySalesRow company={company} />}

      {/* Number of Employees Row — Lite only. Full accounts (canViewExtendedData)
          show the multi-year headcount as a row in the Financials card (TD-2277),
          so it is omitted here to avoid duplicating it. */}
      {!canViewExtendedData && (
        <SummaryEmployeesRow company={company} canViewExtendedData={canViewExtendedData} />
      )}

      {/* Top Management Row (when canViewExtendedData) */}
      {canViewExtendedData && (
        <SummaryTopManagementRow
          topManagement={topManagement}
          isLoadingDetails={isLoadingDetails}
        />
      )}
    </div>
  );
};

GeneralTabSummaryCard.propTypes = {
  company: PropTypes.object,
  /** From the details call; falls back to `company.ownershipSummary`. */
  ownershipSummary: PropTypes.object,
  /**
   * Commercial-register court, from the company-detail payload
   * (`companyDetails.trade_register_court`) rather than from `company`. Absent for
   * users without details access, where the Register row degrades to number-only.
   */
  registerCourt: PropTypes.string,
  isLoadingSupplementaryData: PropTypes.bool,
  loadSupplementaryDataError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** WZ code resolved by CompanyDetails from the industry-code tree */
  industryCode: PropTypes.object,
  isLoadingDetails: PropTypes.bool,
  topManagement: PropTypes.array,
  canViewExtendedData: PropTypes.bool,
  companyGoogleMapsLink: PropTypes.string
};

export default GeneralTabSummaryCard;
